import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { adminApi } from '../../services/api';
import { selectHasSuperAdminAccess } from '../../store/slices/authSlice';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import { format } from 'date-fns';
import { STATUS_OPTIONS as STATUSES } from '../../utils/statusLabels';
import toast from 'react-hot-toast';

/* ─────────────────────────────────────────────────────────────────────────
   SalesforceSyncModal
   PLATFORM_SUPER_ADMIN must select a CLIENT account from our database before
   triggering a Salesforce sync. The chosen account's ID is sent as clientId
   so every finished good fetched in that run is assigned to that company.
───────────────────────────────────────────────────────────────────────── */
function SalesforceSyncModal({ onClose, onSynced, syncMode }) {
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const isFullSync = syncMode === 'full';

  // Fetch active CLIENT companies from our DB
  const { data: accountsRes, isLoading: accountsLoading } = useQuery(
    ['salesforce-accounts'],
    () => adminApi.getSalesforceAccounts(),
    { staleTime: 30_000 }
  );
  const accounts = accountsRes?.data ?? [];

  const syncMutation = useMutation(
    () => isFullSync
      ? adminApi.salesforceFullSync(selectedAccountId)
      : adminApi.salesforceSync(selectedAccountId),
    {
      onSuccess: (res) => {
        const summary = res?.data || res?.message || 'Sync complete';
        toast.success(`✅ Salesforce ${isFullSync ? 'full sync' : 'sync'} completed — ${summary}`);
        onSynced();
        onClose();
      },
      onError: (err) => {
        const timedOut = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
        const msg = timedOut
          ? `The ${isFullSync ? 'full catalog ' : ''}sync is taking longer than 10 minutes. The server may still be processing it; check the Salesforce sync logs before retrying.`
          : err?.response?.data?.error || 'Sync failed. Check logs for details.';
        toast.error(`❌ ${msg}`);
      },
    }
  );

  const selectedName = accounts.find((a) => String(a.id) === String(selectedAccountId))?.companyName;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {isFullSync ? 'Salesforce Full Sync' : 'Salesforce Sync'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Select an account before {isFullSync ? 'refreshing the catalog' : 'syncing'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={syncMutation.isLoading}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none"
          >×</button>
        </div>

        {/* Info banner */}
        <div className={`flex gap-2 rounded-lg px-3 py-2.5 mb-5 text-xs ${
          isFullSync
            ? 'bg-amber-50 border border-amber-200 text-amber-800'
            : 'bg-blue-50 border border-blue-100 text-blue-700'
        }`}>
          <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            {isFullSync ? (
              <>
                This manual-only refresh reads all eligible Finished Goods. Existing records have all
                Salesforce catalog fields refreshed and new records are created. Workflow status,
                assignments, and current client association are not changed.
              </>
            ) : (
              <>
                All new finished goods received in this sync will be assigned to the selected account.
                Only new or changed finished goods since the last sync will be processed.
              </>
            )}
          </span>
        </div>

        {/* Account selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account <span className="text-red-500">*</span>
          </label>
          {accountsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading accounts…
            </div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-red-500 py-2">No active client accounts found. Create one first.</p>
          ) : (
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Select account —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.companyName}</option>
              ))}
            </select>
          )}
        </div>

        {/* Confirm selection chip */}
        {selectedName && (
          <div className="flex items-center gap-2 mb-5 px-3 py-2 bg-green-50 border border-green-100 rounded-lg text-xs text-green-700">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            New Finished Goods will be assigned to: <strong>{selectedName}</strong>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={syncMutation.isLoading}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => syncMutation.mutate()}
            disabled={!selectedAccountId || syncMutation.isLoading}
            className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {syncMutation.isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                {isFullSync ? 'Refreshing catalog…' : 'Syncing catalog…'}
              </>
            ) : (
              <>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isFullSync ? 'Start Full Sync' : 'Start Sync'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   FinishedGoodsList — main page
───────────────────────────────────────────────────────────────────────── */
// ── Reusable sortable header cell ────────────────────────────────────
function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="px-2.5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-800 hover:bg-gray-100 transition-colors"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? <span className="text-blue-500">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
          : <span className="text-gray-300"> ⇅</span>}
      </span>
    </th>
  );
}

export default function FinishedGoodsList() {
  const navigate       = useNavigate();
  const queryClient    = useQueryClient();
  const canSyncSalesforce = useSelector(selectHasSuperAdminAccess);
  const [searchParams, setSearchParams] = useSearchParams();

  // Restore filters/sort/page/search from the URL so that navigating to a
  // detail page and back (browser back, or the list's own history entry)
  // lands on the exact same view instead of resetting to a blank search.
  const [page, setPage]         = useState(() => Number(searchParams.get('page')) || 0);
  const [status, setStatus]     = useState(searchParams.get('status') || '');
  const [sortCol, setSortCol]   = useState(searchParams.get('sortCol') || 'createdAt');
  const [sortDir, setSortDir]   = useState(searchParams.get('sortDir') || 'desc');
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(0);
  };
  const [clientId, setClientId] = useState(searchParams.get('clientId') || '');
  const [search, setSearch]     = useState(searchParams.get('search') || '');
  const [factoryCode, setFactoryCode] = useState(searchParams.get('factoryCode') || '');
  const [factoryName, setFactoryName] = useState(searchParams.get('factoryName') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [debouncedFactoryCode, setDebouncedFactoryCode] = useState(searchParams.get('factoryCode') || '');
  const [debouncedFactoryName, setDebouncedFactoryName] = useState(searchParams.get('factoryName') || '');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncMode, setSyncMode] = useState('incremental');
  const openSyncModal = (mode) => {
    setSyncMode(mode);
    setShowSyncModal(true);
  };

  // Debounce search input — wait 400ms after user stops typing before hitting the API.
  // Only reset to page 0 when the debounced value actually changes — this effect
  // also runs once on mount (e.g. when returning from the detail page), and if we
  // reset the page unconditionally there, it stomps on the page number just
  // restored from the URL.
  const prevDebouncedSearchRef = useRef({
    search: searchParams.get('search') || '',
    factoryCode: searchParams.get('factoryCode') || '',
    factoryName: searchParams.get('factoryName') || '',
  });
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = {
        search: search.trim(),
        factoryCode: factoryCode.trim(),
        factoryName: factoryName.trim(),
      };
      if (next.search !== prevDebouncedSearchRef.current.search
          || next.factoryCode !== prevDebouncedSearchRef.current.factoryCode
          || next.factoryName !== prevDebouncedSearchRef.current.factoryName) {
        prevDebouncedSearchRef.current = next;
        setDebouncedSearch(next.search);
        setDebouncedFactoryCode(next.factoryCode);
        setDebouncedFactoryName(next.factoryName);
        setPage(0);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search, factoryCode, factoryName]);

  // Keep the URL in sync with the current filters/sort/page. Using
  // `replace: true` updates the existing history entry instead of pushing a
  // new one on every keystroke/page change, so the back button from the
  // detail page still returns here in a single step — with this exact state.
  useEffect(() => {
    const next = {};
    if (page)            next.page = String(page);
    if (status)           next.status = status;
    if (clientId)         next.clientId = clientId;
    if (debouncedSearch)  next.search = debouncedSearch;
    if (debouncedFactoryCode) next.factoryCode = debouncedFactoryCode;
    if (debouncedFactoryName) next.factoryName = debouncedFactoryName;
    if (sortCol !== 'createdAt') next.sortCol = sortCol;
    if (sortDir !== 'desc')      next.sortDir = sortDir;
    setSearchParams(next, { replace: true });
  }, [page, status, clientId, debouncedSearch, debouncedFactoryCode, debouncedFactoryName, sortCol, sortDir]);

  const { data, isLoading, isFetching } = useQuery(
    ['finished-goods', page, status, debouncedSearch, debouncedFactoryCode, debouncedFactoryName, clientId, sortCol, sortDir],
    () => adminApi.getFinishedGoods({
      page,
      size: 15,
      status: status && status !== 'REJECTED' ? status : undefined,
      rejected: status === 'REJECTED' ? true : undefined,
      search: debouncedSearch || undefined,
      factoryCode: debouncedFactoryCode || undefined,
      factoryName: debouncedFactoryName || undefined,
      clientId: clientId || undefined,
      sort: `${sortCol},${sortDir}`,
    }),
    { keepPreviousData: true }
  );

  const content    = data?.data?.content || [];
  const totalPages = data?.data?.totalPages || 0;

  return (
    <div className="space-y-5">
      {/* Sync modal */}
      {showSyncModal && (
        <SalesforceSyncModal
          onClose={() => setShowSyncModal(false)}
          onSynced={() => queryClient.invalidateQueries(['finished-goods'])}
          syncMode={syncMode}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finished Goods</h1>
          <p className="text-sm text-gray-500 mt-0.5">All incoming requests from Salesforce</p>
        </div>

        {/* Manual Salesforce actions — never invoked by the scheduler. */}
        {canSyncSalesforce && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openSyncModal('incremental')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Salesforce Sync
            </button>
            <button
              onClick={() => openSyncModal('full')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M12 3v12m0 4h.01M5.64 5.64a9 9 0 1012.72 0" />
              </svg>
              Salesforce Full Sync
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by FG Code, Drop Code, collection, vendor, factory, category, client…"
            className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {isFetching && debouncedSearch && (
            <svg className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-blue-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          )}
        </div>
        <input
          value={factoryCode}
          onChange={(e) => setFactoryCode(e.target.value)}
          placeholder="Filter Factory Code"
          aria-label="Filter by factory code"
          className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          value={factoryName}
          onChange={(e) => setFactoryName(e.target.value)}
          placeholder="Filter Factory Name"
          aria-label="Filter by factory name"
          className="w-52 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        {isLoading ? <LoadingSpinner /> : (
          <>
            {/* Horizontal scrolling is limited to the wide table; pagination
                remains a full-width footer after the table content. */}
            <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] table-fixed text-sm">
              <colgroup>
                <col className="w-[10%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[8%]" />
                <col className="w-[12%]" />
                <col className="w-[8%]" />
                <col className="w-[7%]" />
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortTh col="bobFinishedGoodCode" label="FG Code"     sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="collectionName"       label="Collection"  sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="category"             label="Category"    sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="vendorCode"           label="Vendor Code" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="vendorName"           label="Vendor Name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="factoryCode"          label="Factory Code" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="factoryName"          label="Factory Name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="productDropCode"      label="Drop Code"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="status"               label="Status"      sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="createdAt"            label="Created"     sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="updatedAt"            label="Updated"     sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {content.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-16 text-center text-gray-400 text-sm">
                      No finished goods found
                    </td>
                  </tr>
                ) : content.map((fg) => {
                  const daysSinceSync = fg.createdAt
                    ? Math.floor((Date.now() - new Date(fg.createdAt)) / 86_400_000)
                    : 0;
                  const overdueRow = fg.status === 'NEW' && daysSinceSync >= 3;
                  return (
                  <tr key={fg.id} className="transition-colors hover:bg-gray-50">
                    {/* FG Code */}
                    <td className="px-2.5 py-3 font-mono text-xs whitespace-nowrap">
                      <div className="relative group inline-block">
                        <button
                          type="button"
                          onClick={() => navigate(`/platform/finished-goods/${fg.id}`)}
                          className={`${overdueRow ? 'text-red-600 font-bold' : 'text-blue-700'} hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 rounded-sm`}
                          aria-label={`View finished good ${fg.bobFinishedGoodCode || fg.id}`}>
                          {fg.bobFinishedGoodCode || '—'}
                        </button>
                        {overdueRow && (
                          <span className="absolute bottom-full left-0 mb-1.5 hidden group-hover:block bg-red-700 text-white text-xs font-normal rounded-md px-2.5 py-1.5 whitespace-nowrap z-20 shadow-lg pointer-events-none">
                            ⚠ No action since sync — {daysSinceSync} day{daysSinceSync !== 1 ? 's' : ''} ago
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Collection */}
                    <td className="px-2.5 py-3 text-xs text-gray-600 whitespace-normal break-words" title={fg.collectionName}>{fg.collectionName || '—'}</td>
                    {/* Category */}
                    <td className="px-2.5 py-3 text-xs text-gray-600 whitespace-normal break-words">{fg.category || '—'}</td>
                    {/* Vendor Code */}
                    <td className="px-2.5 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{fg.vendorCode || '—'}</td>
                    {/* Vendor Name */}
                    <td className="px-2.5 py-3 text-xs text-gray-600 whitespace-normal break-words" title={fg.vendorName}>{fg.vendorName || '—'}</td>
                    {/* Factory Code */}
                    <td className="px-2.5 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{fg.factoryCode || '—'}</td>
                    {/* Factory Name */}
                    <td className="px-2.5 py-3 text-xs text-gray-600 whitespace-normal break-words" title={fg.factoryName}>{fg.factoryName || '—'}</td>
                    {/* Product Drop Code */}
                    <td className="px-2.5 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{fg.productDropCode || '—'}</td>
                    {/* Status */}
                    <td className="min-w-0 px-2 py-3"><StatusBadge status={fg.status} /></td>
                    {/* Created */}
                    <td className="px-2 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {fg.createdAt ? format(new Date(fg.createdAt), 'dd MMM yyyy') : '—'}
                    </td>
                    {/* Updated */}
                    <td className="px-2 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {fg.updatedAt ? format(new Date(fg.updatedAt), 'dd MMM yyyy') : '—'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
